import React, { useEffect, useState } from 'react';
import { Modal, Row, Col, Form, Typography, TimePicker } from 'antd';
import type { FormInstance } from 'antd';
import CustomSelect from '@/components/CustomSelect';
import { getFormattedUserTimeZone } from '@/common/utils/utils';
import dayjs from 'dayjs';
import {
  AlertRecipientSelector,
  NotifyViaPills,
} from '@/pages/Shipments/PerShipmentNotification/components';
import { CustomButton, CustomTitle } from '@/components';
import ScheduleFrequencySelector from './ScheduleFrequencySelector';
import { ReportScheduleFormPayload, ScheduleFormValues } from '../types';
import { LeftTailArrowIcon } from '@/assets/icons/icons';
import RecipientValidationField from '@/pages/Shipments/PerShipmentNotification/components/RecipientValidationField';

interface RawUser {
  id: string;
  [key: string]: unknown;
}

interface TeamUserPayload {
  id?: string | number;
  select_all?: boolean;
  users?: RawUser[];
}

export type ScheduleSendTo = {
  teams_users?: TeamUserPayload[];
  additional?: { key: string; enabled?: boolean }[];
};

export type Schedule = {
  repeat_days?: string[];
  id?: string | number;
  name?: string;
  repeat_every?: number;
  repeat_unit?: 'DAYS' | 'WEEKS' | 'MONTHS';
  frequency?: string;
  description: string;
  time_1?: string | null;
  time_2?: string | null;
  time_3?: string | null;
  monthly_day?: number | null;
  monthly_time?: string | null;
  notification_method?: string | null;
  send_to?: ScheduleSendTo | null;
};

const repeatUnitOptions = [
  { label: 'Days', value: 'DAYS' },
  { label: 'Weeks', value: 'WEEKS' },
  { label: 'Months', value: 'MONTHS' },
];

interface ScheduleDownloadModalProps {
  open: boolean;
  mode: 'new' | 'edit';
  initialValues?: Schedule | null;
  onCancel: () => void;
  onSubmit: (values: ReportScheduleFormPayload) => void;
  recipientData: unknown;
  loggedInUserId: string;
  onDelete?: (id?: string | number | null) => void;
}
const { Text } = Typography;
const ScheduleDownloadModal: React.FC<ScheduleDownloadModalProps> = ({
  open,
  mode,
  initialValues,
  onCancel,
  onSubmit,
  recipientData,
  loggedInUserId,
  onDelete,
}) => {
  const [form] = Form.useForm();
  const repeatUnit = Form.useWatch('repeat_unit', form);
  const [selectedFrequency, setSelectedFrequency] = useState<string>(
    initialValues?.frequency || 'ONCE_A_DAY'
  );

  const [selectedNotificationMethod, setSelectedNotificationMethod] =
    useState<string>(initialValues?.notification_method || '');

  const [repeatDays, setRepeatDays] = useState<string[]>(
    initialValues?.repeat_days || []
  );

  const [recipientsValues, setRecipientsValues] = useState<unknown[]>([]);

  const handleNotificationChange = (method: string) => {
    setSelectedNotificationMethod(method);
    setSelectedRecipients([]);
    setRecipientsValues([]);
  };
  const weekDays = [
    { label: 'Mo', value: 'MON' },
    { label: 'Tu', value: 'TUE' },
    { label: 'We', value: 'WED' },
    { label: 'Th', value: 'THU' },
    { label: 'Fr', value: 'FRI' },
    { label: 'Sa', value: 'SAT' },
    { label: 'Su', value: 'SUN' },
  ];

  const [isRecipientsDropdownOpen, setIsRecipientsDropdownOpen] =
    useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;

    if (mode === 'new') {
      form.resetFields();
      form.setFieldsValue({
        repeat_every: 1,
        repeat_unit: 'DAYS',
        frequency: 'ONCE_A_DAY',
        notification_method: undefined,
        time_1: undefined,
        time_2: undefined,
        time_3: undefined,
        monthly_day: undefined,
        monthly_time: undefined,
      });
      setSelectedFrequency('ONCE_A_DAY');
      setSelectedNotificationMethod('');
      setRepeatDays([]);
      setSelectedRecipients([]);
      setRecipientsValues([]);
    }
  }, [open, mode, form]);

  // Edit mode: populate the form from the selected schedule when the modal opens.
  // Depends only on open/mode/initialValues — not `teams`, which
  // AlertRecipientSelector re-sends on every recipient change and which used to
  // reset the user's edits back to the saved schedule.
  useEffect(() => {
    if (!open || mode !== 'edit' || !initialValues) return;

    setSelectedFrequency(initialValues.frequency || 'ONCE_A_DAY');
    setSelectedNotificationMethod(initialValues.notification_method || '');
    setRepeatDays(initialValues.repeat_days || []);

    const recipientsArr: string[] = [];
    const formValues: unknown[] = [];

    const rawSendTo = initialValues?.send_to;

    const teamsUsers: TeamUserPayload[] = Array.isArray(rawSendTo)
      ? rawSendTo
      : Array.isArray(rawSendTo?.teams_users)
        ? rawSendTo.teams_users
        : [];

    // Checkbox state is derived from `selectedRecipients` inside
    // AlertRecipientSelector, so only the recipient keys are built here.
    teamsUsers.forEach((teamItem) => {
      const obj = JSON.parse(JSON.stringify(teamItem));

      if (teamItem.select_all) {
        recipientsArr.push(`${teamItem.id}::ALL_MEMBERS`);
        obj.users = [];
      } else if (Array.isArray(teamItem.users) && teamItem.users.length) {
        const userIds: string[] = [];

        teamItem.users.forEach((u) => {
          recipientsArr.push(`${teamItem.id}::${u.id}`);
          userIds.push(u.id);
        });

        obj.users = userIds;
      }

      formValues.push(obj);
    });

    setSelectedRecipients(recipientsArr);
    setRecipientsValues(formValues);

    form.setFieldsValue({
      repeat_every: initialValues.repeat_every ?? 1,
      repeat_unit: initialValues.repeat_unit ?? 'DAYS',
      frequency: initialValues.frequency ?? 'ONCE_A_DAY',
      notification_method: initialValues.notification_method || undefined,
      time_1: initialValues.time_1
        ? dayjs(initialValues.time_1, 'h:mm a')
        : undefined,
      time_2: initialValues.time_2
        ? dayjs(initialValues.time_2, 'h:mm a')
        : undefined,
      time_3: initialValues.time_3
        ? dayjs(initialValues.time_3, 'h:mm a')
        : undefined,
      monthly_day: initialValues.monthly_day ?? undefined,
      monthly_time: initialValues.monthly_time
        ? dayjs(initialValues.monthly_time, 'h:mm a')
        : undefined,
    });
  }, [open, initialValues, mode, form]);

  const repeatUnitApiMap: Record<string, string> = {
    DAYS: 'days',
    WEEKS: 'weeks',
    MONTHS: 'months',
  };

  const weekDayLabelMap: Record<string, string> = {
    MON: 'Mon',
    TUE: 'Tue',
    WED: 'Wed',
    THU: 'Thu',
    FRI: 'Fri',
    SAT: 'Sat',
    SUN: 'Sun',
  };

  const handleFinish = (values: ScheduleFormValues) => {
    const repeat_unit_raw = values.repeat_unit || 'DAYS';
    const repeat_unit = repeatUnitApiMap[repeat_unit_raw] || repeat_unit_raw;

    const isMonthly = repeat_unit_raw === 'MONTHS';

    const time_1 = isMonthly
      ? values.monthly_time
        ? values.monthly_time.format('h:mm a')
        : null
      : values.time_1
        ? values.time_1.format('h:mm a')
        : null;

    const time_2 = values.time_2 ? values.time_2.format('h:mm a') : null;
    const time_3 = values.time_3 ? values.time_3.format('h:mm a') : null;

    const monthly_on_day = values.monthly_day
      ? `${values.monthly_day} Day`
      : null;

    const weekly_on_days =
      repeat_unit_raw === 'WEEKS'
        ? repeatDays.map((d) => weekDayLabelMap[d] || d)
        : [];

    const payload: ReportScheduleFormPayload = {
      notification_method: selectedNotificationMethod,
      send_to: recipientsValues,
      frequency: selectedFrequency,
      time_1,
      time_2,
      time_3,
      monthly_on_day,

      weekly_on_days,
      repeat_every: values.repeat_every,
      repeat_unit,
      id: initialValues?.id ?? null,
      timezone: getFormattedUserTimeZone().replace(/^Timezone set to\s*/i, ''),
    };

    onSubmit(payload);
  };

  const TimezoneNote = () => (
    <Text style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
      {getFormattedUserTimeZone()}
    </Text>
  );

  const monthlyDayOptions = Array.from({ length: 31 }, (_, i) => ({
    label: `Day ${i + 1}`,
    value: i + 1,
  }));

  const repeatEveryOptions = Array.from({ length: 10 }, (_, i) => ({
    label: String(i + 1),
    value: i + 1,
  }));

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      width={552}
      footer={null}
      destroyOnClose
    >
      <div className='report-schedule-section'>
        <button
          type='button'
          onClick={onCancel}
          className='report-schedule-back'
        >
          <LeftTailArrowIcon color='var(--text-primary)' />
        </button>

        <CustomTitle
          text={`${mode === 'edit' ? 'Edit' : 'Schedule'} recurring download`}
          fontSize='20px'
          lineHeight='28px'
          fontWeight='700'
          marginBottom='0'
        />
      </div>

      <Form
        layout='vertical'
        form={form}
        initialValues={{
          repeat_every: initialValues?.repeat_every || 1,
          repeat_unit: initialValues?.repeat_unit || 'DAYS',
          frequency: initialValues?.frequency || 'ONCE_A_DAY',
          notification_method: initialValues?.notification_method || undefined,
          time_1: initialValues?.time_1
            ? dayjs(initialValues.time_1, 'h:mm a')
            : undefined,
          time_2: initialValues?.time_2
            ? dayjs(initialValues.time_2, 'h:mm a')
            : undefined,
          time_3: initialValues?.time_3
            ? dayjs(initialValues.time_3, 'h:mm a')
            : undefined,
          monthly_day: initialValues?.monthly_day ?? undefined,
          monthly_time: initialValues?.monthly_time
            ? dayjs(initialValues.monthly_time, 'h:mm a')
            : undefined,
        }}
        onFinish={handleFinish}
      >
        <div className='report-schedule-repeat-row'>
          <div className='report-schedule-repeat-count'>
            <Form.Item
              label='Repeat every'
              name='repeat_every'
              rules={[{ required: true, message: 'Please enter a value' }]}
              className='font-black-color report-form-item-flush'
            >
              <CustomSelect
                options={repeatEveryOptions}
                placeholder='Select'
                height='36px'
                bgColor='var(--light-gray)'
                border='0px'
                borderRadius='8px'
                width='100%'
              />
            </Form.Item>
          </div>

          <div className='report-schedule-repeat-unit'>
            <Form.Item
              label=' '
              name='repeat_unit'
              rules={[{ message: 'Please select a unit' }]}
              className='font-black-color report-form-item-flush'
            >
              <CustomSelect
                options={repeatUnitOptions}
                placeholder='Days'
                height='36px'
                bgColor='var(--light-gray)'
                border='0px'
                borderRadius='8px'
                width='100%'
              />
            </Form.Item>
          </div>
        </div>
        {repeatUnit === 'WEEKS' && (
          <Form.Item label='Repeat on' className='report-form-item-top'>
            <div className='report-repeat-days'>
              {weekDays.map((day, index) => {
                const isActive = repeatDays.includes(day.value);
                const isLast = index === weekDays.length - 1;

                return (
                  <div
                    key={day.value}
                    className={`report-repeat-day${isActive ? ' is-active' : ''}${isLast ? ' is-last' : ''}`}
                    onClick={() => {
                      setRepeatDays((prev) =>
                        prev.includes(day.value)
                          ? prev.filter((d) => d !== day.value)
                          : [...prev, day.value]
                      );
                    }}
                  >
                    {day.label}
                  </div>
                );
              })}
            </div>
          </Form.Item>
        )}

        {repeatUnit === 'MONTHS' ? (
          <>
            <div className='report-schedule-section'>
              <Form.Item
                label='Monthly on'
                name='monthly_day'
                rules={[{ required: true, message: 'Please select a day' }]}
                className='font-black-color'
              >
                <CustomSelect
                  options={monthlyDayOptions}
                  placeholder='Day 1'
                  height='36px'
                  bgColor='var(--light-gray)'
                  border='0px'
                  borderRadius='8px'
                  width='100%'
                />
              </Form.Item>
            </div>
            <div className='report-schedule-section'>
              <Form.Item
                label='Send at'
                name='monthly_time'
                rules={[{ required: true, message: 'Please select a time' }]}
              >
                <TimePicker
                  className='frequency-input report-time-picker'
                  suffixIcon={null}
                  use12Hours
                  format='h:mm a'
                />
              </Form.Item>
              <div className='report-timezone-note'>
                <TimezoneNote />
              </div>
            </div>
          </>
        ) : (
          <div className='report-schedule-frequency'>
            <ScheduleFrequencySelector
              selectedFrequency={selectedFrequency}
              setSelectedFrequency={setSelectedFrequency}
            />
          </div>
        )}

        <div className='report-schedule-section'>
          <NotifyViaPills
            text='Send via'
            notificationType='non-ai'
            form={form as FormInstance}
            setNotificationMethod={handleNotificationChange}
            resetOnChange={handleNotificationChange}
            disabledMethods={['WHATSAPP']}
          />
        </div>

        <RecipientValidationField
          form={form}
          selectedRecipients={selectedRecipients}
        >
          <AlertRecipientSelector
            hideAdditionalRecipients
            alertType='REPORT_SCHEDULE'
            form={form}
            loggedInUserId={loggedInUserId}
            recipientData={recipientData}
            isRecipientsDropdownOpen={isRecipientsDropdownOpen}
            setIsRecipientsDropdownOpen={setIsRecipientsDropdownOpen}
            sendTeamsToParent={() => {}}
            notificationMethod={selectedNotificationMethod}
            showAdditionalTP={false}
            selectedRecipients={selectedRecipients}
            setSelectedRecipients={setSelectedRecipients}
            setRecipientsValues={setRecipientsValues}
            setAdditional={() => {}}
            recipientsValues={recipientsValues}
          />
        </RecipientValidationField>

        <Row
          justify='end'
          style={{
            marginTop: 16,
            display: 'flex',
            gap: 8,
          }}
        >
          <Col>
            <CustomButton
              text='Cancel'
              bgColor='transparent'
              color='var(--text-primary)'
              fontSize='14px'
              fontWeight='600'
              boxShadow='none'
              height='36px'
              onClick={onCancel}
            />
          </Col>

          {mode === 'edit' && (
            <Col>
              <CustomButton
                text='Delete'
                bgColor='transparent'
                color='var(--text-primary)'
                fontSize='14px'
                fontWeight='600'
                boxShadow='none'
                height='36px'
                onClick={() => {
                  if (initialValues?.id && onDelete) {
                    onDelete(initialValues.id);
                  }
                }}
              />
            </Col>
          )}

          <Col>
            <CustomButton
              htmlType='submit'
              text={mode === 'edit' ? 'Save' : 'Schedule'}
              bgColor='var(--text-primary)'
              color='var(--surface-container)'
              fontSize='14px'
              fontWeight='600'
              height='36px'
              borderRadius='8px'
              padding='0 32px'
              // onClick={() => form.submit()}
            />
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ScheduleDownloadModal;
